import { useTranslation } from 'react-i18next';
import {
	Typography,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddExerciseDialog from './components/AddExerciseDialog';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import CloseIcon from '@mui/icons-material/Close';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import SetDetailOrderDialog from './components/SetDetailOrderDialog';
import { useDeleteHealthDetailListMutation } from '@/api/app-setting/healthApiSlice';
import {
	HealthDetailResultDTO,
	HealthExerciseDTO,
} from '@/api/DTO/app-setting/health.interface';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

export interface Props {
	exerciseList: HealthExerciseDTO[];
}

export default function HealthDetailListSummaryPage({ exerciseList }: Props) {
	const { t } = useTranslation();
	const [deleteHealthDetailList] = useDeleteHealthDetailListMutation();
	const [selectedRows, setSelectedRows] = useState<any[]>([]);

	const showModal = useModal();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('moment.off'),
		},
		{
			id: OnoffType.on,
			name: t('moment.on'),
		},
	];

	const exerciseTypeList = [
		{
			id: 1,
			name: t('moment.meditation'),
		},
		{
			id: 2,
			name: t('moment.body'),
		},
		{
			id: 3,
			name: t('moment.crossover'),
		},
	];

	const transformResponse = useCallback(
		(response?: HealthDetailResultDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	const getLink = (row: any) => {
		if (row.exerciseType == 1) {
			return `/exercise-manage/meditation-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		if (row.exerciseType == 2) {
			return `/exercise-manage/body-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		if (row.exerciseType == 3) {
			return `/exercise-manage/crossover-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		return '';
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('moment.exerciseTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseType',
			headerName: t('moment.exerciseType'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.exerciseType, exerciseTypeList),
		},
		{
			field: 'seriesTitleZh',
			headerName: t('moment.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseTime',
			headerName: t('moment.seriesTime'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('moment.pushStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, onoffList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('moment.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('moment.createTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link to={getLink(params.row)}>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	const handleSelect = (rows: any[]) => {
		setSelectedRows(rows);
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('moment.deleteExercise')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('moment.isDeleteExercise')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteHealthDetailList(selectedRows);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMomentSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						setSelectedRows([]);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h6'>{t('moment.exerciseList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<Button
						disabled={selectedRows.length < 1}
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<DeleteOutlined />}
						onClick={() => handleDelete()}
					>
						{t('button.delete')}
					</Button>
					<SetDetailOrderDialog exerciseList={exerciseList} />
					<AddExerciseDialog />
				</Stack>
			</Box>

			<Box marginTop={2}>
				<LocalDataGrid
					rows={transformResponse(exerciseList || []) ?? []}
					columns={columns}
					rowCount={0}
					getRowId={(row: { id: any }) => row.id}
					pagination={false}
					onSelectionModelChange={handleSelect}
					checkboxSelection
					selectionModel={selectedRows}
				/>
			</Box>
		</>
	);
}
